import React, { useMemo, useState } from 'react';
import Layout from '../components/Layout';
import UserAvatar from '../components/UserAvatar';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api, { formatDate } from '../utils/api';
import { Camera, Check, Eye, EyeOff, KeyRound, Layers, Mail, Scissors, ShieldCheck, Video, X } from 'lucide-react';

const formatError = (detail) => {
  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg || JSON.stringify(item)).join(' | ');
  }
  if (typeof detail === 'string') return detail;
  return 'Please try again';
};

const ROLE_LABELS = { admin: 'Admin', media_head: 'Media head', team_member: 'Team member' };
const SPECIALIZATIONS = {
  photo: { label: 'Photography', icon: Camera },
  video: { label: 'Videography', icon: Video },
  editing: { label: 'Editing', icon: Scissors },
  other: { label: 'Other', icon: Layers }
};

const passwordStrength = (value) => {
  if (!value) return { score: 0, label: '' };
  let score = 0;
  if (value.length >= 6) score += 1;
  if (value.length >= 10) score += 1;
  if (/[A-Z]/.test(value) && /[a-z]/.test(value) && /\d/.test(value)) score += 1;
  if (/[^A-Za-z0-9]/.test(value)) score += 1;
  const capped = Math.min(score, 3);
  return { score: capped, label: ['Too short', 'Okay', 'Good', 'Strong'][value.length < 6 ? 0 : capped] };
};

function PasswordInput({ id, value, onChange, placeholder, autoComplete, invalid, autoFocus }) {
  const [show, setShow] = useState(false);
  return (
    <div className="password-field">
      <input
        id={id}
        type={show ? 'text' : 'password'}
        className={`field-input ${invalid ? 'has-error' : ''}`}
        placeholder={placeholder}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        value={value}
        onChange={onChange}
        aria-invalid={invalid || undefined}
        required
      />
      <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? 'Hide password' : 'Show password'}>
        {show ? <EyeOff size={17} /> : <Eye size={17} />}
      </button>
    </div>
  );
}

const ChangePassword = () => {
  const { user } = useAuth();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const strength = useMemo(() => passwordStrength(newPassword), [newPassword]);
  const mismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;
  const matches = confirmPassword.length > 0 && confirmPassword === newPassword;
  const sameAsOld = newPassword.length > 0 && newPassword === oldPassword;
  const checks = [
    { ok: newPassword.length >= 6, label: 'At least 6 characters' },
    { ok: /[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword), label: 'Upper and lower case letters' },
    { ok: /\d/.test(newPassword), label: 'At least one number' },
    { ok: newPassword.length > 0 && !sameAsOld, label: 'Different from your current password' }
  ];
  const canSubmit = oldPassword && newPassword.length >= 6 && matches && !sameAsOld && !loading;

  const spec = SPECIALIZATIONS[user?.specialization];
  const SpecIcon = spec?.icon;

  const reset = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (sameAsOld) {
      toast.error('Choose a password different from your current one');
      return;
    }

    try {
      setLoading(true);
      await api.post('/auth/change-password', {
        old_password: oldPassword,
        new_password: newPassword
      });
      toast.success('Password updated successfully');
      setUpdatedAt(new Date());
      reset();
    } catch (error) {
      toast.error('Failed to update password', {
        description: formatError(error.response?.data?.detail)
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="dashboard-page institutions-page">
        <div className="dashboard-heading">
          <div>
            <p className="page-eyebrow">Your account</p>
            <h1>Account settings</h1>
            <p>Your profile details and sign-in security.</p>
          </div>
        </div>

        {updatedAt && (
          <div className="success-note" role="status">
            <span>
              <Check size={16} />
            </span>
            <p>
              <strong>Password updated.</strong> Use the new password the next time you sign in.
            </p>
            <button className="footer-refresh" onClick={() => setUpdatedAt(null)} aria-label="Dismiss">
              <X size={15} />
            </button>
          </div>
        )}

        <div className="create-layout account-layout">
          <aside className="create-aside" aria-label="Profile">
            <section className="dashboard-panel">
              <p className="preview-label">Profile</p>
              <article className="person-card preview-card">
                <div className="person-top">
                  <UserAvatar id={user?.id} name={user?.name} size={58} className="person-avatar" />
                </div>
                <h3 className="person-name">{user?.name || 'Your name'}</h3>
                <p className="person-email">
                  <Mail size={14} />
                  {user?.email}
                </p>
                <div className="person-pills">
                  <span className={`event-status role-${user?.role}`}>
                    <i />
                    {ROLE_LABELS[user?.role] || user?.role}
                  </span>
                  {spec && (
                    <span className="event-status spec-pill">
                      <SpecIcon size={12} />
                      {spec.label}
                    </span>
                  )}
                </div>
                {user?.created_at && (
                  <div className="person-foot">
                    <span>Member since {formatDate(user.created_at)}</span>
                  </div>
                )}
              </article>
              <p className="preview-note">
                Your name, email and role are managed by an admin. Ask them if something needs changing.
              </p>
            </section>
          </aside>

          <form className="dashboard-panel create-form" onSubmit={handleSubmit} noValidate>
            <section className="form-section" aria-labelledby="pw-title">
              <div className="form-section-head account-section-head">
                <span className="sheet-icon">
                  <KeyRound size={20} strokeWidth={1.7} />
                </span>
                <div>
                  <h2 id="pw-title">Change password</h2>
                  <p>Confirm your current password, then choose a new one.</p>
                </div>
              </div>

              <div className="field">
                <label htmlFor="old-password">Current password</label>
                <PasswordInput
                  id="old-password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  placeholder="Enter your current password"
                  autoComplete="current-password"
                />
              </div>

              <div className="field-row account-row">
                <div className="field">
                  <label htmlFor="new-password">New password</label>
                  <PasswordInput
                    id="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimum 6 characters"
                    autoComplete="new-password"
                    invalid={sameAsOld}
                  />
                  {newPassword && (
                    <div className="strength" data-score={strength.score}>
                      <span />
                      <span />
                      <span />
                      <em>{strength.label}</em>
                    </div>
                  )}
                </div>
                <div className="field">
                  <label htmlFor="confirm-password">Confirm new password</label>
                  <PasswordInput
                    id="confirm-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat the new password"
                    autoComplete="new-password"
                    invalid={mismatch}
                  />
                  {mismatch && <small className="field-error">Passwords do not match</small>}
                  {matches && (
                    <small className="field-ok">
                      <Check size={13} /> Passwords match
                    </small>
                  )}
                </div>
              </div>

              <ul className="pw-checks" aria-label="Password requirements">
                {checks.map((c) => (
                  <li key={c.label} className={c.ok ? 'is-ok' : ''}>
                    <span>{c.ok ? <Check size={12} strokeWidth={3} /> : null}</span>
                    {c.label}
                  </li>
                ))}
              </ul>
            </section>

            <div className="form-actions">
              <Button type="button" variant="ghost" onClick={reset} disabled={loading}>
                Clear
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {loading ? 'Updating…' : 'Update password'}
              </Button>
            </div>

            <p className="admin-note">
              <ShieldCheck size={15} />
              You stay signed in on this device after changing your password.
            </p>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default ChangePassword;
